import BigNumber from 'bignumber.js';
import { PlusIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import firebaseStore from '@client/stores/firebaseStore';
import modalStore from '@client/stores/modalStore';
import Box from '@client/uikit/Box';
import Frame from '@client/uikit/Frame';
import { apiClient } from '@app/apiClient';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import { useCommonNavigate } from '@client/component/CommonLink';
import Jsonld from '../Jsonld';
import MetaHeader from '@client/component/MetaHeader';
import AIRecommendModalContent from './AIRecommendModalContent';
import BeginnerTip from './components/BeginnerTip';
import { StrategyInfoEditBox } from './components/StrategyInfoSection';
import StrategyStockActions from './components/StrategyStockActions';
import { StockSearchResult } from './StockSearchInput';
import StrategyWeightInput, { WeightItem } from './StrategyWeightInput';

class ViewStore {
    strategyName = '';
    description = '';
    weights: WeightItem[] = [];
    isSubmitting = false;

    constructor() {
        makeAutoObservable(this);
    }

    setStrategyName(name: string) {
        this.strategyName = name;
    }

    setDescription(desc: string) {
        this.description = desc;
    }

    addStock(stock: StockSearchResult) {
        // 이미 추가된 종목인지 확인
        const exists = this.weights.some((w) => w.ticker === stock.ticker);
        if (exists) {
            return;
        }

        // 새 종목 추가 (기본 비중 30%)
        runInAction(() => {
            this.weights.push({
                ticker: stock.ticker,
                name: stock.nameKo || stock.name,
                weight: 0.3,
            });
        });
    }

    addStocks(stocks: StockSearchResult[]) {
        stocks.forEach((stock) => this.addStock(stock));
    }

    updateWeight(ticker: string, weight: number) {
        const item = this.weights.find((w) => w.ticker === ticker);
        if (item) {
            item.weight = weight;
        }
    }

    removeStock(ticker: string) {
        this.weights = this.weights.filter((w) => w.ticker !== ticker);
    }

    get totalWeight() {
        // BigNumber로 정확한 합계 계산
        return this.weights
            .reduce((sum, item) => sum.plus(item.weight), new BigNumber(0))
            .toNumber();
    }

    get canSubmit() {
        return this.weights.length > 0 && this.totalWeight > 0 && this.totalWeight <= 1;
    }

    async submit() {
        if (!this.canSubmit) {
            return;
        }

        this.isSubmitting = true;

        try {
            const result = await apiClient.strategy.member.create.mutate({
                name: this.strategyName || undefined,
                description: this.description || undefined,
                weights: Object.fromEntries(this.weights.map((w) => [w.ticker, w.weight])),
            });

            console.log('전략 생성 완료:', result);

            return true;
        } catch (error) {
            console.error('전략 생성 실패:', error);
            return false;
        } finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }
}

const StrategyCreatePage = observer(() => {
    const viewStore = useMobxInstance(ViewStore);
    const { navigate } = useCommonNavigate();

    useEffect(() => {
        // 초기화
    }, [viewStore]);

    const title = '투자 전략 만들기';
    const description = '투자 아이디어를 실험하고 수익률을 확인해보세요.';

    const handleAIRecommend = async () => {
        const { returnValue } = await modalStore.custom<StockSearchResult[]>({
            title: 'AI 종목 추천',
            renderComponent: ({ modalId }) => (
                <AIRecommendModalContent
                    modalId={modalId}
                    excludeTickers={viewStore.weights.map((w) => w.ticker)}
                />
            ),
            showCloseButton: true,
            preventClose: false,
        });

        if (returnValue && returnValue.length > 0) {
            viewStore.addStocks(returnValue);
        }
    };

    const handleSubmit = async () => {
        const success = await viewStore.submit();
        if (success) {
            // 생성 후 홈으로 이동
            navigate('/');
        }
    };

    return (
        <Frame>
            <MetaHeader title={title} description={description} />
            <Jsonld type="Blog" title={title} description={description} />
            <Frame.Title title={title} />

            {/* 종목 구성 (최우선) */}
            <Box>
                <Box.Title title="종목 구성" />

                {/* 팁: 종목이 없을 때만 표시 */}
                {viewStore.weights.length === 0 && <BeginnerTip />}

                {/* 비중 입력 */}
                <StrategyWeightInput
                    weights={viewStore.weights}
                    onWeightChange={(ticker, weight) => viewStore.updateWeight(ticker, weight)}
                    onRemove={(ticker) => viewStore.removeStock(ticker)}
                />

                {/* AI 추가 버튼 + 검색 입력 */}
                <StrategyStockActions
                    weights={viewStore.weights}
                    onAIRecommend={handleAIRecommend}
                    onStockSelect={(stock) => viewStore.addStock(stock)}
                />
            </Box>

            {/* 추가 정보 (접을 수 있음) */}
            <StrategyInfoEditBox
                initialExpanded={false}
                strategyName={viewStore.strategyName}
                description={viewStore.description}
                onStrategyNameChange={(name) => viewStore.setStrategyName(name)}
                onDescriptionChange={(desc) => viewStore.setDescription(desc)}
            />

            {/* 생성 버튼 */}
            <Box>
                <button
                    className="btn btn-primary btn-lg btn-block"
                    disabled={!viewStore.canSubmit || viewStore.isSubmitting}
                    onClick={async (e) => {
                        e.preventDefault();

                        // 비로그인으로도 만들 수 있게 하고, 생성할 때 로그인하게 유도
                        const isLoggedIn = await firebaseStore.isLoggedIn();
                        if (isLoggedIn) {
                            handleSubmit();
                        } else {
                            const { returnValue } = await modalStore.login();
                            if (returnValue) {
                                handleSubmit();
                            }
                        }
                    }}
                >
                    {viewStore.isSubmitting ? (
                        <>
                            <span className="loading loading-spinner loading-sm"></span>
                            생성 중...
                        </>
                    ) : (
                        <>
                            <PlusIcon className="size-5" />
                            전략 만들기
                        </>
                    )}
                </button>
            </Box>
        </Frame>
    );
});

export default StrategyCreatePage;
